/**
 * FormShell - the chrome every full-page form in the app wears.
 *
 * Header (title + description) sitting at the top of a scrollable body, and a
 * `glass-bar` action footer pinned underneath it. Extracted from `BaseForm` so
 * forms that can't use BaseForm's schema-driven field rendering (TemplateForm's
 * split pane, wizards with bespoke bodies) get the same chrome instead of
 * hand-copying it - which is how the copies drifted onto raw `bg-white` /
 * `bg-gray-*` colours in the first place.
 *
 * The shell owns layout and tokens only; the caller owns the body and the
 * footer buttons.
 */
import React from 'react';
import clsx from 'clsx';

export interface FormShellProps {
  /** Root element. `form` when the shell wraps a submitting form. */
  as?: 'form' | 'div';
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  /** Headline shown above the body. Omitted (or `hideHeader`) → no header. */
  title?: React.ReactNode;
  /** Muted line under the title. */
  description?: React.ReactNode;
  /** Optional glyph rendered in a rounded chip to the left of the title. */
  icon?: React.ReactNode;
  hideHeader?: boolean;
  /** Content column width - applied to both the body and the footer row. */
  maxWidth?: string;
  /** Extra classes on the body's padded inner wrapper. */
  bodyClassName?: string;
  /** Footer content (buttons). Falsy → the footer bar is not rendered. */
  footer?: React.ReactNode;
  /** Layout classes for the footer's inner row. */
  footerClassName?: string;
  /**
   * Centre the body vertically in the space above the footer - for a short
   * form that is one field (Feedback), which otherwise hangs at the top of an
   * empty page. A taller body still scrolls from the top.
   */
  centered?: boolean;
  /** Absolutely-positioned overlay (submission loader) rendered over the shell. */
  overlay?: React.ReactNode;
  /**
   * Sizing for the root. The shell only sets `flex flex-col relative` - the
   * caller decides whether it fills its parent (`h-full`) or flexes
   * (`flex-1 min-h-0`).
   */
  className?: string;
  children: React.ReactNode;
}

const FormShell: React.FC<FormShellProps> = ({
  as = 'div',
  onSubmit,
  title,
  description,
  icon,
  hideHeader = false,
  maxWidth = 'max-w-3xl',
  bodyClassName,
  footer,
  footerClassName = 'justify-between',
  centered = false,
  overlay,
  className,
  children,
}) => {
  const showHeader = !hideHeader && (title || description);
  // `min-h-0` matters when the shell is itself a flex item: a column flex item
  // defaults to `min-height: auto`, which is content-based and quietly beats an
  // explicit `h-full`. Without it the shell grows past its parent, the body
  // never becomes the overflow container, and the footer bar is pushed out of
  // view instead of staying pinned.
  const rootClassName = clsx('flex flex-col relative min-h-0', className);

  const content = (
    <>
      {overlay}

      <div
        className={clsx(
          'flex-1 !overflow-y-auto custom-scrollbar-main min-h-0',
          centered && 'flex flex-col'
        )}
      >
        {/* `my-auto` in a column flex: centred while there is room, and simply
            top-aligned and scrolling once there is not. */}
        <div className={clsx('mx-auto w-full', maxWidth, centered && 'my-auto')}>
          {showHeader && (
            <div className="px-6 pt-6 pb-4 border-b border-hairline">
              <div className="flex items-center gap-3">
                {icon && (
                  <span
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg
                               border border-hairline-soft bg-champagne/10 text-champagne
                               [&>svg]:h-5 [&>svg]:w-5"
                  >
                    {icon}
                  </span>
                )}
                <div className="min-w-0">
                  {/* The settings type scale (SETTINGS_PAGE_*): medium, not
                      semibold, and room between title and description. */}
                  {title && <h2 className="text-lg font-medium text-fg-strong">{title}</h2>}
                  {description && <p className="mt-1.5 text-sm font-normal text-fg-muted">{description}</p>}
                </div>
              </div>
            </div>
          )}

          <div className={clsx('px-6 py-6 space-y-6', bodyClassName)}>{children}</div>
        </div>
      </div>

      {footer && (
        <div className="glass-bar border-t border-hairline px-6 py-4 flex-shrink-0">
          <div className={clsx('mx-auto w-full flex gap-2', maxWidth, footerClassName)}>
            {footer}
          </div>
        </div>
      )}
    </>
  );

  if (as === 'form') {
    return (
      <form onSubmit={onSubmit} className={rootClassName}>
        {content}
      </form>
    );
  }

  return <div className={rootClassName}>{content}</div>;
};


export default FormShell;
